/**
 * BETA Tool Suite Registry — Frontend Mirror
 *
 * Kept in sync manually with `riskninja-backend/src/workflows/suites.ts`,
 * the same way TOOL_CONFIG/TOOL_INFO already mirror backend workflow
 * metadata. See REMAINING_BETA_SUITES_PLAN.md Phase 0 item 3.
 */

export interface SuiteDefinition {
  id: string;
  displayName: string;
  autoType: string;
  memberTypes: string[];
}

export const SUITES: SuiteDefinition[] = [
  {
    id: 'cyber',
    displayName: 'Cyber',
    autoType: 'cyber_auto',
    memberTypes: ['cyber_auto', 'cyber_benchmark', 'cyber_renewal_delta', 'cyber_war_systemic', 'cyber_ransomware_trap', 'cyber_quote_comparison'],
  },
  {
    id: 'do',
    displayName: 'D&O',
    autoType: 'do_auto',
    memberTypes: ['do_auto', 'do_benchmark', 'do_renewal_delta', 'do_distress_scanner', 'do_emerging_exposure'],
  },
  {
    id: 'home',
    displayName: 'Home Insurance',
    autoType: 'home_auto',
    memberTypes: [
      'home_auto',
      'home_owner_benchmark',
      'home_tenant_condo_benchmark',
      'home_quote_comparison',
      'home_renewal_delta',
      'home_dp_rental_benchmark',
      'home_dp_rental_renewal_delta',
      'home_hnw_benchmark',
    ],
  },
  {
    id: 'plines',
    displayName: 'Personal Lines',
    autoType: 'plines_auto',
    memberTypes: ['plines_auto', 'pa_benchmark', 'pa_quote_comparison', 'pa_renewal_delta', 'umb_benchmark', 'umb_quote_comparison', 'umb_renewal_delta'],
  },
  {
    id: 'rt',
    displayName: 'Requirements Consulting',
    autoType: 'rt_auto',
    memberTypes: ['rt_auto', 'rt_requirements_reviewer', 'rt_conformance_alignment', 'rt_compliance_verifier'],
  },
  {
    id: 'premdev',
    displayName: 'Premium Development',
    autoType: 'premdev_auto',
    memberTypes: [
      'premdev_auto',
      'premdev_workers_comp',
      'premdev_prof_liability',
      'premdev_general_liability',
      'premdev_property',
      'premdev_package_bop',
    ],
  },
  {
    id: 'lossrun',
    displayName: 'Loss Runs',
    autoType: 'lossrun_auto',
    memberTypes: ['lossrun_auto', 'lossrun_fact_engine', 'lossrun_mitigation_wc'],
  },
  {
    id: 're',
    displayName: 'Real Estate',
    autoType: 're_auto',
    memberTypes: ['re_auto', 're_policy_extraction', 're_risk_transfer', 're_br_financials', 're_zone_extractor', 're_portfolio_brief'],
  },
  {
    id: 'cl',
    displayName: 'Commercial Lines (P&C)',
    autoType: 'cl_auto',
    memberTypes: [
      'cl_auto',
      'cl_gl_benchmark',
      'cl_property_benchmark',
      'cl_commercial_auto_benchmark',
      'cl_inland_marine_benchmark',
      'cl_excess_benchmark',
      'cl_package_benchmark',
      'cl_bop_benchmark',
      'cl_nytpao_flow',
      'cl_package_quote_comparison',
      'cl_bop_quote_comparison',
      'cl_xarch_quote_comparison',
      'cl_package_renewal_delta',
      'cl_bop_renewal_delta',
    ],
  },
  {
    id: 'ca',
    displayName: 'Coverage & Claims Analysis',
    autoType: 'ca_auto',
    memberTypes: ['ca_auto', 'ca_coverage_determination', 'ca_portfolio_determination', 'ca_carrier_position_audit'],
  },
  {
    id: 'ntr',
    displayName: 'Notice Trigger Registers',
    autoType: 'ntr_auto',
    memberTypes: ['ntr_auto', 'ntr_first_party', 'ntr_property_liability', 'ntr_mgmt_professional'],
  },
];

const TYPE_TO_SUITE = new Map<string, SuiteDefinition>();
for (const suite of SUITES) {
  for (const type of suite.memberTypes) {
    TYPE_TO_SUITE.set(type, suite);
  }
}

export function getSuiteForType(type: string | undefined | null): SuiteDefinition | undefined {
  if (!type) return undefined;
  return TYPE_TO_SUITE.get(type);
}

/** True for any workflow type belonging to any BETA suite (member or its `*_auto` router). */
export function isSuiteWorkflowType(type: string | undefined | null): boolean {
  return !!getSuiteForType(type);
}

export const ALL_SUITE_TYPES: string[] = SUITES.flatMap((s) => s.memberTypes);
